import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal, ViewEncapsulation } from '@angular/core';
import { injectTranslation } from '@skylab-angular-shared/core';
import { TranslatePipe } from '@skylab-angular-shared/pipes';
import { ToastDelay } from '@skylab-shared/ui';
import { calculateFileSize, FileSizeUnit } from '../file-picker/filepicker.component-ins';
import { ToastService } from '../toast/toast.service';
import { IconComponent } from '../icon/icon.component';

@Component({
	selector: 'skl-file-upload-area',
	template: `
		@let fileNameValue = fileName();
		@let err = errorMessage();

		<div
			(click)="!fileNameValue && fileUploadInput.click()"
			(drop)="onFileDrop($event)"
			(dragover)="onDragOver($event)"
		>
			<input #fileUploadInput type="file" class="hidden-input" [accept]="accept()" (change)="onFileSelected($event)">

			@if (!fileNameValue) {
				<div class="file-upload-area">
					<ng-content select="[slot='file-upload']"/>
					@if (accept()) {
						<span class="accept-format">{{ 'ui.file-upload-area.accept-format-label' | translate }}: {{ accept() }}</span>
					}
				</div>
			} @else {
				<div class="file-upload-area no-hover" [class.error]="err" [class.success]="!err">
					<div class="success-icon-wrapper">
						<skl-icon name="check" class="success-icon" size="20px" />
					</div>
					<div class="selected-filename">{{ fileNameValue }}</div>
					<button class="remove-button" (click)="$event.stopPropagation(); removeFile()">
						<skl-icon name="remove" size="16px" />
						{{ 'ui.file-upload-area.remove-button' | translate }}
					</button>
				</div>
				@if (err) {
					<div class="error-message">
						<skl-icon name="exclamation-circle" size="14px" />
						{{ err }}
					</div>
				}
			}
		</div>
	`,
	styles: `
		:host {
			--border-color: var(--line-2-color);
			--upload-bg: var(--surface-2-color);

			--upload-bg-hover: light-dark(oklch(97.6% 0.002 264), oklch(26.6% 0.005 264));
			--upload-bg-active: light-dark(oklch(96.6% 0.005 264), oklch(26.6% 0.005 264));
			--upload-border-active: light-dark(oklch(56.3% 0.22 300), oklch(76.3% 0.11 264));
			--upload-icon-color: light-dark(oklch(88.3% 0.004 264), oklch(46.3% 0.007 264));
			--upload-format-color: light-dark(oklch(68.3% 0.007 264), oklch(68.3% 0.007 264));

			display: block;
		}

		.file-upload-area {
			border: 1px dashed var(--border-color);
			border-radius: 0.5rem;
			height: min(8.125em, 20vh);
			display: flex;
			flex-direction: column;
			align-items: center;
			justify-content: center;
			text-align: center;
			cursor: pointer;
			transition: all 0.2s;
			position: relative;
			background-color: var(--upload-bg);
			gap: var(--gutter-small);

			&:not(.no-hover):hover {
				border-color: var(--accent-blue);
				background-color: var(--upload-bg-hover);
			}

			&.no-hover {
				cursor: default;
			}

			&.success {
				border-style: solid;
			}

			&.error {
				border-color: var(--error-color);
			}

			.upload-icon {
				color: var(--upload-icon-color);
				margin-bottom: .75em;
			}

			.message {
				font-weight: 500;
				color: var(--accent-blue);
				font-size: .875em;
			}

			.accept-format {
				color: var(--upload-format-color);
				font-size: .75em;
				text-transform: uppercase;
				font-weight: 500;
			}

			.success-icon-wrapper {
				width: 42px;
				height: 42px;
				border-radius: 50%;
				background-color: oklch(from var(--success-color) l c h / 0.1);
				display: flex;
				align-items: center;
				justify-content: center;
				margin-bottom: 8px;

				.success-icon {
					color: var(--success-color);
				}
			}
		}

		.selected-filename {
			font-weight: 700;
			color: var(--text-minor-color);
			font-size: 14px;
			margin-bottom: 4px;
		}

		.error-message {
			display: flex;
			gap: .25em;
			white-space: pre-line;
			color: var(--error-color);
			font-size: 13px;
			font-weight: 500;
			margin: 5px 0px 15px 0px;
		}

		.remove-button {
			all: unset;
			display: grid;
			place-items: center;
			grid-auto-flow: column;
			gap: .25em;
			color: var(--error-color);
			font-size: 13px;
			cursor: pointer;
			font-weight: 500;
			transition: text-decoration 0.2s;
			background-color: transparent;
			border: 1px solid transparent;
			border-radius: .25em;
			padding: .25em .5em;

			&:hover {
				border-color: oklch(from var(--error-color) l c h / 0.2);
				background-color: rgba(220, 20, 60, 0.1);
			}
		}

		.hidden-input {
			display: none;
		}
	`,
	imports: [TranslatePipe, IconComponent],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.ShadowDom,
})
export class FileUploadAreaComponent {
	protected readonly _T = injectTranslation();
	readonly #toastService = inject(ToastService);

	public readonly accept = input<string | null>(null);
	public readonly maxFileSize = input<{value: number, unit: FileSizeUnit} | null>(null);
	public readonly errorMessage = input<string | null>(null);

	public readonly fileChange = output<File | null>();

	public readonly calculatedMaxFileSize = computed(() => {
		const config = this.maxFileSize();
		if (!config) return null;

		const { value, unit } = config;

		return calculateFileSize(value, unit);
	});

	public readonly fileName = signal('');
	readonly #file = signal<File | null>(null);

	public onFileSelected(event: Event) {
		const file = (event.target as HTMLInputElement).files?.[0];
		if (file) {
			const maxFileSize = this.calculatedMaxFileSize();
			if (maxFileSize && file.size > maxFileSize) {
				this.#showErrorToast(file.name, 'size');
				(event.target as HTMLInputElement).value = '';
				return;
			}

			if (!this.#isValidFileType(file)) {
				this.#showErrorToast(file.name, 'type');
				(event.target as HTMLInputElement).value = '';
				return;
			}

			this.#handleFile(file);

			(event.target as HTMLInputElement).value = '';
		}
	}

	public onFileDrop(event: DragEvent) {
		event.preventDefault();
		const file = event.dataTransfer?.files[0];
		const maxFileSize = this.calculatedMaxFileSize();
		if (file) {
			if (maxFileSize && file.size > maxFileSize) {
				this.#showErrorToast(file.name, 'size');
				return;
			}

			if (!this.#isValidFileType(file)) {
				this.#showErrorToast(file.name, 'type');
				return;
			}

			this.#handleFile(file);
		}
	}

	public onDragOver(event: DragEvent) {
		event.preventDefault();
		event.stopPropagation();
	}


	protected removeFile() {
		this.#file.set(null);
		this.fileName.set('');
		this.fileChange.emit(null);
	}

	#handleFile(file: File) {
		this.#file.set(file);
		this.fileName.set(file.name);
		this.fileChange.emit(file);
	}

	#showErrorToast(fileName: string, errorType: 'size' | 'type' = 'type') {
		this.#toastService.showAsync({
			type: 'error',
			text: this._T(`ui.file-upload-area.error-${errorType}`, { fileName }),
			duration: ToastDelay.Short,
		});
	}

	#isValidFileType(file: File): boolean {
		const accept = this.accept();
		if (!accept) {
			return true;
		}

		const allowedExtensions = accept.split(',').map(ext => ext.trim().toLowerCase());
		const fileName = file.name.toLowerCase();
		return allowedExtensions.some(ext => fileName.endsWith(ext));
	}

}
